import { getHumanFriendlyTicks } from '@design/charts/helpers/chart-ticks/chart-ticks';
import { Button } from '@design/components/button/Button';
import * as d3 from 'd3';
import { useState } from 'react';

import { Axis } from './axis/Axis';
import {
  BAR_CHART_WIDTH,
  BAR_HEIGHT,
  CHART_WIDTH,
  LABEL_HEIGHT,
  MAX_DISPLAY_ITEMS,
  margin,
  PORTFOLIO_ITEM_LEFT_PADDING,
  ROW_HEIGHT,
} from './constants';
import styles from './HorizontalBarChart.module.scss';
import { ChartHeader } from './header/ChartHeader';
import { ChartItem } from './item/ChartItem';
import { assureZeroInDomain, formatPortfolioData } from './utils';

/** One row in the horizontal-bar chart. */
export interface MappedDominoV2 {
  /** Visible label for the row (rendered to the left of the bar). */
  label: string;
  /** Label shown inside the row's tooltip. */
  tooltipLabel: string;
  /** Optional small tag rendered next to the label (e.g. ticker, category). */
  tag?: string;
  /** Numeric value used for the bar length and the value pill. */
  median: number;
}
export interface HorizontalBarChartPropsV2 {
  /** Rows to render. When `data.length` exceeds the limit, the rest are hidden behind a "view all" toggle. */
  data: MappedDominoV2[];
  /** Optional rows pinned at the top above a divider (e.g. portfolio aggregates above individual holdings). */
  headerData?: MappedDominoV2[];
  /** Optional title rendered above the header rows. */
  headerTitle?: string;
  /** Optional sub-label rendered under `headerTitle`. */
  headerLabel?: string;
  /** Label shown next to the median tick line on the axis. */
  medianLabel: string;
  /** Translated text for the "view all" expand button. */
  viewAllText: string;
  /** Translated text for the "view less" collapse button. */
  viewLessText: string;
  /** Called when a row is clicked. */
  onChartItemClick?: (mappedDomino: MappedDominoV2) => void;
  /** When true, treats values as already in display units (skips the `* 100` percentage scaling). */
  useOriginalValue?: boolean;
  /** Prefix for positive value pills. Defaults to `'+'`. */
  prefix?: string;
}

/**
 * Horizontal bar chart with optional header rows, in-place value pills,
 * and an expand/collapse toggle when there are more rows than the
 * configured display limit.
 *
 * Bars extend left or right of zero based on `median`'s sign.
 * Designed for ranked / portfolio-style data where the row label
 * matters as much as the value.
 */
export const HorizontalBarChartV2 = ({
  data,
  headerData = [],
  headerTitle,
  headerLabel,
  medianLabel,
  viewAllText,
  viewLessText,
  onChartItemClick,
  useOriginalValue = false,
  prefix = '+',
}: HorizontalBarChartPropsV2) => {
  const [expanded, setExpanded] = useState(false);
  const onViewAllHandle = () => setExpanded(true);
  const hasMaximum = data.length > MAX_DISPLAY_ITEMS;
  const values = data.map(i => i.median);
  const headerValues = headerData.map(i => i.median);
  const TOTAL_HEADER_HEIGHT = headerData.length * ROW_HEIGHT;

  const HEADER_TITLE_HEIGHT = headerTitle ? ROW_HEIGHT : 0;
  const portfolioData = formatPortfolioData({ data, hasMaximum, expanded });
  const viewAllButtonHeight = hasMaximum ? ROW_HEIGHT : 0;
  const height =
    (portfolioData.length + headerData.length) * ROW_HEIGHT +
    LABEL_HEIGHT * 2 +
    margin.top +
    margin.bottom +
    viewAllButtonHeight +
    HEADER_TITLE_HEIGHT;
  const minValue = Math.min(...values, ...headerValues);
  const maxValue = Math.max(...values, ...headerValues);

  const [adjustedMin, adjustMax] = assureZeroInDomain([minValue, maxValue]);

  const { maxRounded, minRounded, chartTicks } = getHumanFriendlyTicks({
    min: adjustedMin,
    max: adjustMax,
  });

  const verticalCoordinateOfContent =
    margin.top + ROW_HEIGHT + TOTAL_HEADER_HEIGHT;

  const assetRowListHeight = portfolioData.length * ROW_HEIGHT;

  const axisHeight =
    verticalCoordinateOfContent +
    assetRowListHeight +
    margin.bottom -
    (expanded ? ROW_HEIGHT : 0);

  const xScale = d3
    .scaleLinear()
    .domain([minRounded, maxRounded])
    .range([margin.left, BAR_CHART_WIDTH]);

  const yScale = d3
    .scaleBand()
    .domain(portfolioData.map(d => d.label))
    .range([ROW_HEIGHT, axisHeight]);

  return (
    <svg
      className={styles.rootSvg}
      width={CHART_WIDTH}
      height={height}
      data-testid="high-low-chart"
    >
      <ChartHeader
        headerData={headerData}
        headerTitle={headerTitle}
        xScale={xScale}
        yScale={yScale}
        prefix={prefix}
      />
      <Axis
        transform={`translate(0, ${margin.top + HEADER_TITLE_HEIGHT})`}
        headerX={CHART_WIDTH - margin.right}
        headerLabel={medianLabel}
        xScale={xScale}
        tickValues={chartTicks}
        height={axisHeight}
        useOriginalValue={useOriginalValue}
        prefix={prefix}
      />

      <g
        data-testid="chart-bars-root"
        width={CHART_WIDTH}
        transform={`translate(0, ${HEADER_TITLE_HEIGHT})`}
      >
        <text
          className={styles.labelHeader}
          x={margin.left + PORTFOLIO_ITEM_LEFT_PADDING}
          y={BAR_HEIGHT}
        >
          {headerLabel}
        </text>

        {portfolioData.map((d, i) => (
          <ChartItem
            key={d.tag || i}
            data={d}
            index={i}
            hasMaximum={hasMaximum && !expanded}
            xScale={xScale}
            yScale={yScale}
            viewAllText={viewAllText}
            onViewAllHandle={onViewAllHandle}
            onItemClick={onChartItemClick}
            useOriginalValue={useOriginalValue}
            prefix={prefix}
          />
        ))}
      </g>

      {expanded && (
        <foreignObject
          y={verticalCoordinateOfContent + assetRowListHeight}
          width="200"
          height="36"
        >
          <Button
            data-testid="view-less-btn"
            label={viewLessText}
            variant="empty"
            size="xsmall"
            onClick={() => setExpanded(false)}
          />
        </foreignObject>
      )}
    </svg>
  );
};
